import "core-js/modules/esnext.iterator.constructor.js";
import "core-js/modules/esnext.iterator.filter.js";
import "core-js/modules/esnext.iterator.find.js";
import "core-js/modules/esnext.iterator.map.js";
import { unref as _unref, renderSlot as _renderSlot, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock } from "vue";
import { ref, reactive, computed, onUnmounted, nextTick, watch } from 'vue';
import PQueue from 'p-queue';
import useCosUpload from '@/hooks/useCosUpload';
import messageUtil from '@/utils/message';

// Props 定义

export default {
  __name: 'index',
  props: {
    autoUpload: {
      type: Boolean,
      default: false
    },
    accept: {
      type: Array,
      default: () => [],
      required: true
    },
    multiple: {
      type: Boolean,
      default: true
    },
    maxDuration: {
      type: Number,
      default: 300
    },
    maxSize: {
      type: Number,
      default: 500 // 500M
    },
    maxLimit: {
      type: Number,
      default: 10
    },
    message: {
      type: String,
      default: ''
    },
    fileList: {
      type: Array,
      default: () => []
    },
    drag: {
      type: Boolean,
      default: false
    }
  },
  emits: ['loading', 'upload-state', 'upload', 'fileProgress', 'filesSelected'],
  setup(__props, {
    expose: __expose,
    emit: __emit
  }) {
    const props = __props;

    // Emits 定义
    const emit = __emit;

    // 响应式数据
    const fileName = ref('');
    const list = ref([]);
    const uploadRef = ref(null);

    // 使用 COS 上传 composable
    const cosUpload = useCosUpload();

    // 计算属性
    const acceptStr = computed(() => props.accept.join(','));
    const loading = computed(() => cosUpload.uploadState.isUploading);

    // 计算可上传的文件数量
    const uploadableFiles = computed(() => {
      return list.value.filter(file => file.status !== 'success' && file.status !== 'uploading');
    });

    // 是否有文件需要上传
    const hasFilesToUpload = computed(() => {
      return uploadableFiles.value.length > 0;
    });

    // 监听 fileList prop 变化，同步到内部状态
    watch(() => props.fileList, val => {
      list.value = val || [];
    }, {
      immediate: true,
      deep: true
    });

    /**
     * 检查文件大小
     * @param {number} bytes - 文件大小（字节）
     * @returns {boolean} 是否通过检查
     */
    const checkSize = bytes => {
      if (bytes === 0) {
        messageUtil.warning(`文件不能为空`);
        return false;
      }
      const sizeInMb = bytes / (1024 * 1024);
      if (sizeInMb > props.maxSize) {
        if (sizeInMb > 1024) {
          messageUtil.warning(`上传${fileName.value}大小不能超过${props.maxSize / 1024}GB`);
        } else {
          messageUtil.warning(`上传${fileName.value}大小不能超过 ${props.maxSize}MB`);
        }
        return false;
      }
      return true;
    };

    /**
     * 清理上传状态
     */
    const clearUploadState = () => {
      list.value = [];
      uploadRef.value?.clearFiles();
      cosUpload.resetUploadState();
    };

    /**
     * 移除特定文件 - 确保el-upload内部状态同步
     * @param {string} fileId - 文件ID或UID
     */
    // const removeFile = fileId => {
    //   // 1. 更新我们的响应式状态
    //   list.value = list.value.filter(file => file.fileId !== fileId && file.uid !== fileId)

    //   // 2. 关键：直接操作 el-upload 的内部状态
    //   if (uploadRef.value?.uploadFiles) {
    //     const index = uploadRef.value.uploadFiles.findIndex(
    //       uf => uf.uid === fileId || uf.name === fileId,
    //     )
    //     if (index !== -1) {
    //       uploadRef.value.uploadFiles.splice(index, 1)
    //     }
    //   }

    //   // 3. 或者清空后让组件重新同步
    //   uploadRef.value?.clearFiles()
    // }

    /**
     * 更新文件状态
     * @param {string} fileId - 文件ID
     * @param {Object} updates - 要更新的字段
     */
    const updateFileState = (fileId, updates) => {
      const index = list.value.findIndex(f => f.fileId === fileId || f.uid === fileId);
      if (index !== -1) {
        // 合并更新属性，而不是直接替换整个对象
        list.value[index] = {
          ...list.value[index],
          ...updates
        };
        emit('fileProgress', fileId, list.value[index]);
      }
    };
    const updateFileStateByEvent = (fileId, event, result) => {
      console.log('updateFileStateByEvent', fileId, event, result);
      const fn = {
        taskReady: () => {
          const other = result?.taskId ? {
            taskId: result?.taskId
          } : {};
          updateFileState(fileId, {
            status: 'uploading',
            // progress: 0,
            ...other
          });
        },
        progress: () => {
          updateFileState(fileId, {
            status: 'uploading',
            progress: Math.round(result.percent * 100)
          });
        },
        success: () => {
          updateFileState(fileId, {
            status: 'success',
            progress: 100,
            result
          });
        },
        error: () => {
          updateFileState(fileId, {
            status: 'error'
          });
        },
        cancelled: () => {
          updateFileState(fileId, {
            status: 'cancelled'
          });
        }
      };
      fn?.[event]?.();
    };

    /**
     * 文件上传前的验证
     * @param {File} file - 文件对象
     * @returns {Promise<boolean>} 验证结果
     */
    const beforeUpload = async file => {
      fileName.value = file.name;
      const fileSuffix = file.name.split('.').pop();
      if (list.value.find(item => item.fileName === file.name)) {
        messageUtil.warning(`文件已存在`);
        return false;
      }
      // 1、判断文件格式
      if (file.name && !props.accept.includes(`.${fileSuffix}`)) {
        messageUtil.warning(`文件限制 ${props.accept.join('、')} 格式`);
        return false;
      }

      // 2、判断文件大小
      if (!checkSize(file.size)) {
        return false;
      }
      return true;
    };

    /**
     * 处理文件数量超限
     * @param {Array} files - 文件列表
     */
    const handleExceed = files => {
      messageUtil.warning(`当前限制选择 ${props.maxLimit} 个文件，本次选择了 ${files.length} 个文件`);
    };

    /**
     * 文件列表变化处理（选择文件时）
     * @param {Object} file - 文件对象
     * @param {Array} files - 文件列表
     */
    const changeUpload = async (file, files) => {
      if (!(await beforeUpload(file))) {
        return;
      }
      // 处理本地文件列表
      const fileInfo = {
        uid: file.uid,
        fileName: file.name,
        fileSize: file.size,
        fileFormat: file.name?.split('.').pop(),
        filePath: '',
        file: file.raw || file.file,
        status: 'ready',
        progress: 0
      };
      // list.value.push(fileInfo)
      emit('filesSelected', list.value, fileInfo); // 通知父组件文件已选择
    };

    /**
     * 上传单个文件到 COS
     * @param {Object} fileInfo - 文件信息对象
     * @returns {Promise<{success: boolean, fileId: string, error?: string}>}
     */
    const uploadSingleFile = async fileInfo => {
      console.log('uploadSingleFile', fileInfo);
      const fileId = fileInfo.fileId || fileInfo.uid || `${Date.now()}_${Math.random()}`;
      const fileObj = fileInfo.file || fileInfo;
      const appendInfo = {};
      try {
        // 创建回调函数
        const callback = ({
          data
        }) => {
          const {
            event,
            message
          } = data;
          updateFileStateByEvent(fileId, event, message);
        };
        const uploadResult = await cosUpload.selectFile(fileObj, appendInfo, callback);
        if (!uploadResult.success) {
          updateFileStateByEvent(fileId, 'error');
          return {
            success: false,
            fileId,
            error: uploadResult.error || '上传失败'
          };
        }

        // 更新文件状态为成功
        updateFileStateByEvent(fileId, 'success', uploadResult);
        emit('upload', uploadResult, fileId);
        return {
          success: true,
          fileId,
          result: uploadResult
        };
      } catch (error) {
        console.error('上传失败:', error);
        updateFileStateByEvent(fileId, 'error');
        return {
          success: false,
          fileId,
          error: error.message || '上传异常'
        };
      }
    };

    /**
     * 批量上传所有本地文件
     */
    const batchUploadFiles = async () => {
      if (!hasFilesToUpload.value) {
        messageUtil.warning('请先选择文件');
        return {
          success: false,
          message: '没有可上传的文件'
        };
      }
      console.log('开始批量上传文件:', uploadableFiles.value);

      // 创建并发队列 - 只上传需要上传的文件
      const uploadQueue = new PQueue({
        concurrency: 3
      }); // 降低并发数
      const uploadTasks = uploadableFiles.value.map(fileInfo => uploadQueue.add(() => uploadSingleFile(fileInfo)));
      try {
        const results = await Promise.all(uploadTasks);
        const failedUploads = results.filter(result => !result.success);
        if (failedUploads.length > 0) {
          console.error('上传失败的文件:', failedUploads);
          return {
            success: false,
            failed: failedUploads,
            message: `${failedUploads.length} 个文件上传失败`
          };
        }
        console.log('所有文件上传成功');
        return {
          success: true,
          results
        };
      } catch (error) {
        console.error('批量上传异常:', error);
        return {
          success: false,
          error,
          message: '批量上传过程中发生异常'
        };
      }
    };

    /**
     * 取消特定文件上传
     * @param {string} fileId - 文件ID
     */
    const cancelFileUpload = async fileId => {
      try {
        const file = list.value.find(f => f.uid === fileId);
        if (file) {
          if (file.status === 'uploading') {
            await cosUpload.cancelUpload(file.taskId);
          }
        }
      } catch (error) {
        console.error('取消上传失败:', error);
      }
    };

    /**
     * 取消所有上传
     */
    const cancelAllUploads = async () => {
      try {
        await cosUpload.cancelUpload();
        list.value = [];
        clearUploadState();
      } catch (error) {
        console.error('取消上传失败:', error);
      }
    };

    // 手动选择文件
    const handleSelect = () => {
      const input = uploadRef.value.$el.querySelector('input');
      if (input) {
        input.disabled = false;
        input.click();
      }
    };
    /**
     * 暴露给父组件的方法
     */
    const expose = {
      uploadRef,
      // 核心上传方法
      batchUploadFiles,
      uploadSingleFile,
      cancelAllUploads,
      cancelFileUpload,
      handleSelect,
      // removeFile,

      // 状态管理
      clearUploadState,
      updateFileState,
      uploadState: cosUpload.uploadState,
      canUpload: cosUpload.canUpload,
      hasError: cosUpload.hasError,
      isCompleted: cosUpload.isCompleted,
      loading: cosUpload.uploadState.isUploading,
      resumeUpload: cosUpload.resumeUpload
    };

    // 组件卸载时清理资源
    onUnmounted(() => {
      cosUpload.cleanup();
      messageUtil.clear(); // 清理消息定时器
    });

    // 暴露方法给父组件
    __expose(expose);
    return (_ctx, _cache) => {
      const _component_el_upload = _resolveComponent("el-upload");
      return _openBlock(), _createBlock(_component_el_upload, {
        ref_key: "uploadRef",
        ref: uploadRef,
        action: "#",
        "file-list": list.value,
        accept: acceptStr.value,
        "auto-upload": __props.autoUpload,
        "before-upload": beforeUpload,
        "show-file-list": false,
        "on-change": changeUpload,
        drag: __props.drag,
        "on-exceed": handleExceed,
        multiple: __props.multiple,
        disabled: loading.value || !_unref(cosUpload).canUpload || list.value.length >= props.maxLimit
      }, {
        default: _withCtx(() => [!__props.autoUpload ? _renderSlot(_ctx.$slots, "trigger", {
          key: 0,
          loading: loading.value
        }) : _createCommentVNode("", true)]),
        _: 3
      }, 8, ["file-list", "accept", "auto-upload", "drag", "multiple", "disabled"]);
    };
  }
};